import Toggle from "@vueform/toggle";
import { Options, Vue } from "vue-class-component";

import TableLite from "@/components/table/TableLiteTs.vue";
import { FilterCriteria, FilterForm, TraumaObitoResponse } from "@/domain/trauma.model";
import { UtilDate } from "@/helpers/date";
import { CidService } from "@/services/cid.service";
import { TraumaObitoService } from "@/services/trauma-obito.service";
import MultiSelect from "primevue/multiselect";
import { ModelSelect } from "vue-search-select";
import "vue-search-select/dist/VueSearchSelect.css";
import { useToast } from "vue-toastification";
import { TABLE } from "./table";

@Options({
  name: "Table",
  components: {
    TableLite,
    Toggle,
    MultiSelect,
    ModelSelect,
  },
  watch: {
    perPage: {
      handler(newValue) {
        this.setSearchParams("limit", newValue);
      },
    },
    currentPage: {
      handler(newValue) {
        this.setSearchParams("currentPage", newValue);
      },
    },
    order: {
      handler(newValue) {
        this.setSearchParams("order", newValue);
      },
    },
    sort: {
      handler(newValue) {
        this.setSearchParams("sort", newValue);
      },
    },
  },
})
export default class Table extends Vue {
  private toast = useToast();
  public table = TABLE;

  public maxPerPage = 50;
  public perPage = 15;
  public currentPage = 1;
  public order = null;
  public sort = null;

  public errorMessage = "";
  public hasError = false;
  public modalRegister = { show: false };
  public modalReclassify = { show: false, title: "", options: { agreeClassification: false } };
  public modalReclassifyConfirm = { show: false, title: "" };
  public modalConfirm = { show: false, title: "", action: null, actionCancel: null };
  public showFilters = false;
  public selectedRow = "";
  public reclassificationSelected = "";
  public reclassificationInput = "";
  public disagreeJustificationText = "";
  public isLoading = 0;
  public cids: { text: string; value: string }[] = [];
  public cidsToShow: { text: string; value: string }[] = [];

  private filters: FilterForm = {};
  private filtersNames = [
    "status",
    "concordaReclassificacao",
    "sexo",
    "ufResidencia",
    "numDO",
    "idadeObito",
    "codigoMunicipioResidencia",
    "dataObito",
    "causaBasica",
    "causaBasicaReclassificada",
    "tipoObito",
    "acidenteTrabalho",
    "dataRevisao",
  ];

  public id = "";
  public concordaReclassificacao = false;
  public registro = {
    numDO: "",
    dataObito: "",
    codigoEstabelecimentoOcorrencia: "",
    codigoMunicipioOcorrencia: "",
    ufResidencia: "",
    codigoMunicipioResidencia: "",
    localOcorrencia: "",
    sexo: "",
    dataNascimento: "",
    racaCor: "",
    tipoObito: "",
    tipoObitoRevisado: "",
    tipoObitoRevisadoOriginal: "",
    acidenteTrabalho: "",
    acidenteTrabalhoRevisado: "",
    acidenteTrabalhoRevisadoOriginal: "",
    agrupamentoML: "",
    agrupamentoMLDesc: "",
    linhaA: "",
    linhaB: "",
    linhaC: "",
    linhaD: "",
    linhaII: "",
    codigoCausaBasica: "",
    codigoCausaBasicaOriginal: "",
    codigoCausaBasicaReclassificada: "",
    causaBasica: "",
    causaBasicaOriginal: "",
    causaBasicaReclassificada: "",
    causaBasicaReclassificadaDesc: "",
    status: "",
    dataRevisao: "",
    idade: 0,
    dsEvento: "",
    internacoes: [],
    intoxicacoes: [],
    acidentesTransporte: [],
    acidentesTrabalho: [],
    violencias: [],
  };

  public statusEnum = {
    [0]: "Não revisado",
    [1]: "Revisado",
    [2]: "Reclassificado no SIM",
  };

  public concordaEnum = {
    true: "Sim",
    false: "Não",
  };

  public statusOptions = [
    { name: "Não revisado", code: 0 },
    { name: "Revisado", code: 1 },
    { name: "Reclassificado no SIM", code: 2 },
  ];

  public concordanciaOptions = [
    { name: "Sim", code: "true" },
    { name: "Não", code: "false" },
  ];

  public sexoOptions = [
    { name: "Masculino", code: "masculino" },
    { name: "Feminino", code: "feminino" },
    { name: "Ignorado", code: "ignorado" },
  ];

  public acidTrabOptions = [
    { name: "Sim", code: "Sim" },
    { name: "Não", code: "Não" },
  ];

  public ufOptions = [
    { code: "AC" },
    { code: "AL" },
    { code: "AP" },
    { code: "AM" },
    { code: "BA" },
    { code: "CE" },
    { code: "DF" },
    { code: "ES" },
    { code: "GO" },
    { code: "MA" },
    { code: "MT" },
    { code: "MS" },
    { code: "MG" },
    { code: "PA" },
    { code: "PB" },
    { code: "PR" },
    { code: "PE" },
    { code: "PI" },
    { code: "RJ" },
    { code: "RN" },
    { code: "RS" },
    { code: "RO" },
    { code: "RR" },
    { code: "SC" },
    { code: "SP" },
    { code: "SE" },
    { code: "TO" },
  ];

  created(): void {
    const query = this.$route.query;

    this.perPage = Number(query.limit || this.perPage);
    this.currentPage = Number(query.offset || this.currentPage);
    this.order = query.order || this.order;
    this.sort = query.sort || this.sort;

    this.filters.sexo = query["filter[sexo]"]
      ? Array.isArray(query["filter[sexo]"])
        ? this.sexoOptions.filter(i => query["filter[sexo]"].includes(i.code))
        : this.sexoOptions.filter(i => query["filter[sexo]"] === i.code)
      : [];

    this.filters.status = query["filter[status]"]
      ? Array.isArray(query["filter[status]"])
        ? this.statusOptions.filter(i => query["filter[status]"].includes(i.code.toString()))
        : this.statusOptions.filter(i => query["filter[status]"] === i.code.toString())
      : [];

    this.filters.concordaReclassificacao = query["filter[concordaReclassificacao]"]
      ? Array.isArray(query["filter[concordaReclassificacao]"])
        ? this.concordanciaOptions.filter(i => query["filter[concordaReclassificacao]"].includes(i.code.toString()))
        : this.concordanciaOptions.filter(i => query["filter[concordaReclassificacao]"] === i.code.toString())
      : [];

    this.filters.ufResidencia = query["filter[ufResidencia]"]
      ? Array.isArray(query["filter[ufResidencia]"])
        ? this.ufOptions.filter(i => query["filter[ufResidencia]"].includes(i.code))
        : this.ufOptions.filter(i => query["filter[ufResidencia]"] === i.code)
      : [];

    this.filters.numDO = query["filter[numDO]"]?.toString() || "";
    this.filters.idadeObito = query["filter[idadeObito]"]?.toString() || "";
    this.filters.codigoMunicipioResidencia = query["filter[codigoMunicipioResidencia]"]?.toString() || "";
    this.filters.dataObito = query["filter[dataObito]"]?.toString() || "";
    this.filters.causaBasica = query["filter[causaBasica]"]?.toString() || "";
    this.filters.causaBasicaReclassificada = query["filter[causaBasicaReclassificada]"]?.toString() || "";
    this.filters.tipoObito = query["filter[tipoObito]"]?.toString() || "";
    this.filters.acidenteTrabalho = query["filter[acidenteTrabalho]"]
      ? Array.isArray(query["filter[acidenteTrabalho]"])
        ? this.acidTrabOptions.filter(i => query["filter[acidenteTrabalho]"].includes(i.code))
        : this.acidTrabOptions.filter(i => query["filter[acidenteTrabalho]"] === i.code)
      : [];
    this.filters.dataRevisao = query["filter[dataRevisao]"]?.toString() || "";

    this.filtersNames.forEach(i => {
      if (this.filters[i]?.length) {
        this.showFilters = true;
      }
    });
  }

  mounted(): void {
    this.doSearch(this.currentPage, this.perPage, this.order, this.sort, this.makeFiltersCriteria());
    this.listValidCids();
  }

  public handleSearch() {
    this.doSearch(this.currentPage, this.perPage, this.order, this.sort, this.makeFiltersCriteria());
  }

  public doSearch(offset?: number, limit?: number, order?: string, sort?: string, filter?: FilterCriteria) {
    if (limit > this.maxPerPage) {
      limit = this.maxPerPage;
    }

    this.perPage = limit;
    this.currentPage = offset;
    this.order = order;
    this.sort = sort;

    this.list(offset, limit, order, sort, filter);
  }

  public doSearchSubTable(data: object[], order?: string, sort?: string) {
    data = data.sort((a, b) => {
      const valueA = a[order];
      const valueB = b[order];

      if (valueA === valueB) return 0;

      const multiplier = sort === "asc" ? 1 : -1;

      if (valueA < valueB) {
        return -1 * multiplier;
      }
      if (valueA > valueB) {
        return 1 * multiplier;
      }

      return 0;
    });
  }

  private list(offset: number, limit: number, order?: string, sort?: string, filter?: FilterCriteria) {
    this.showLoading();

    TraumaObitoService.list({
      offset,
      limit,
      order,
      sort,
      filter,
    })
      .then(result => {
        this.table.rows =
          result?.data?.map(row => ({
            ...row,
            dataRevisao: row.dataRevisao
              ? UtilDate.format(row.dataRevisao, "DD/MM/YYYY HH:mm", "America/Sao_Paulo")
              : "-",
            dataObito: row.dataObito ? UtilDate.format(row.dataObito, "DD/MM/YYYY") : "-",
          })) || [];

        this.table.totalRecordCount = result.pagination.total;

        this.makeFiltersRow();
      })
      .catch(error => this.toast.error(error.response?.data?.message || "Ocorreu um erro ao listar os óbitos."))
      .finally(() => this.hideLoading());
  }

  private listValidCids() {
    this.showLoading();

    CidService.listValidCids()
      .then(result => (this.cids = result?.map(i => ({ text: `${i.codigo} - ${i.descricao}`, value: i.codigo })) || []))
      .catch(error => this.toast.error(error.response?.data?.message || "Ocorreu um erro ao carregar a lista de cids."))
      .finally(() => this.hideLoading());
  }

  private showLoading(): void {
    this.isLoading++;
  }

  private hideLoading(): void {
    this.isLoading--;
  }

  public openModal(props: {
    modal: string;
    title?: string;
    data?: TraumaObitoResponse;
    action?: () => void;
    actionCancel?: () => void;
    options?: object;
  }): void {
    this[props.modal].show = true;

    if (props.title) {
      this[props.modal].title = props.title;
    }

    if (props.action) {
      this[props.modal].action = props.action;
    }

    if (props.actionCancel) {
      this[props.modal].actionCancel = props.actionCancel;
    }

    if (props.options) {
      this[props.modal].options = props.options;
    }

    if (props.data) {
      this.id = props.data.idTraumaVitima;

      this.getRegisterById(this.id);

      this.reclassificationInput = "";
      this.disagreeJustificationText = "";
    }

    this.defineCidsInterval(props.options?.["agreeClassification"], this.registro.agrupamentoML);
  }

  public closeModal(modal: string): void {
    this[modal].show = false;
    this.hasError = false;
    this.errorMessage = "";
  }

  public openReclassifyConfirmModal() {
    this.validateReclassificationFields();

    if (!this.hasError) {
      this.reclassificationSelected = this.cids.find(i => i.value === this.reclassificationInput).text;

      const title = "Deseja confirmar a reclassificação?";

      this.openModal({ modal: "modalReclassifyConfirm", title });
    }
  }

  private getRegisterById(id: string): void {
    this.showLoading();

    TraumaObitoService.getObitoById(id)
      .then(traumaObito => {
        if (traumaObito) {
          this.registro.numDO = traumaObito.numDO;
          this.registro.dataObito = traumaObito.dataObito ? UtilDate.format(traumaObito.dataObito, "DD/MM/YYYY") : "-";
          this.registro.codigoEstabelecimentoOcorrencia = traumaObito.codigoEstabelecimentoOcorrencia;
          this.registro.codigoMunicipioOcorrencia = traumaObito.codigoMunicipioOcorrencia;
          this.registro.ufResidencia = traumaObito.ufResidencia;
          this.registro.codigoMunicipioResidencia = traumaObito.codigoMunicipioResidencia;
          this.registro.localOcorrencia = traumaObito.localOcorrencia;
          this.registro.sexo = traumaObito.sexo;
          this.registro.dataNascimento = traumaObito.dataNascimento
            ? UtilDate.format(traumaObito.dataNascimento, "DD/MM/YYYY")
            : "-";
          this.registro.racaCor = traumaObito.racaCor;
          this.registro.tipoObito = traumaObito.tipoObito;
          this.registro.tipoObitoRevisado = traumaObito.tipoObitoRevisado;
          this.registro.tipoObitoRevisadoOriginal = traumaObito.tipoObitoRevisado;
          this.registro.acidenteTrabalho = traumaObito.acidenteTrabalho;
          this.registro.acidenteTrabalhoRevisado = traumaObito.acidenteTrabalhoRevisado;
          this.registro.acidenteTrabalhoRevisadoOriginal = traumaObito.acidenteTrabalhoRevisado;
          this.registro.agrupamentoML = traumaObito.agrupamentoML;
          this.registro.agrupamentoMLDesc = traumaObito.agrupamentoMLDesc;
          this.registro.linhaA = traumaObito.linhaA;
          this.registro.linhaB = traumaObito.linhaB;
          this.registro.linhaC = traumaObito.linhaC;
          this.registro.linhaD = traumaObito.linhaD;
          this.registro.linhaII = traumaObito.linhaII;
          this.registro.causaBasica = traumaObito.causaBasica;
          this.registro.causaBasicaOriginal = traumaObito.causaBasicaOriginal;
          this.registro.causaBasicaReclassificada = traumaObito.causaBasicaReclassificada;
          this.registro.causaBasicaReclassificadaDesc = traumaObito.causaBasicaReclassificadaDesc;
          this.registro.codigoCausaBasica = traumaObito.codigoCausaBasica;
          this.registro.codigoCausaBasicaOriginal = traumaObito.codigoCausaBasicaOriginal;
          this.registro.codigoCausaBasicaReclassificada = traumaObito.codigoCausaBasicaReclassificada;
          this.registro.status = traumaObito.status;
          this.registro.dataRevisao = traumaObito.dataRevisao
            ? UtilDate.format(traumaObito.dataRevisao, "DD/MM/YYYY")
            : "-";
          this.registro.idade = traumaObito.idadeObito;

          this.registro.internacoes = traumaObito.intraHospitalar?.map(i => ({
            ...i,
            dtInter: i.dtInter ? UtilDate.format(i.dtInter, "DD/MM/YYYY HH:mm") : "-",
            dtSaida: i.dtSaida ? UtilDate.format(i.dtSaida, "DD/MM/YYYY HH:mm") : "-",
          }));

          this.registro.acidentesTrabalho = traumaObito.notificAcTrabalho?.map(i => ({
            ...i,
            dtOcor: i.dtOcor ? UtilDate.format(i.dtOcor, "DD/MM/YYYY HH:mm") : "-",
            dataObito: i.dataObito ? UtilDate.format(i.dataObito, "DD/MM/YYYY HH:mm") : "-",
          }));

          this.registro.acidentesTransporte = traumaObito.notificAcTransporte?.map(i => ({
            ...i,
            dtOcor: i.dtOcor ? UtilDate.format(i.dtOcor, "DD/MM/YYYY HH:mm") : "-",
          }));

          this.registro.intoxicacoes = traumaObito.notificIntox?.map(i => ({
            ...i,
            dtOcor: i.dtOcor ? UtilDate.format(i.dtOcor, "DD/MM/YYYY HH:mm") : "-",
            dtInterna: i.dtInterna ? UtilDate.format(i.dtInterna, "DD/MM/YYYY HH:mm") : "-",
            dataObito: i.dataObito ? UtilDate.format(i.dataObito, "DD/MM/YYYY HH:mm") : "-",
          }));

          this.registro.violencias = traumaObito.notificViol?.map(i => ({
            ...i,
            dtOcor: i.dtOcor ? UtilDate.format(i.dtOcor, "DD/MM/YYYY HH:mm") : "-",
          }));

          this.registro.dsEvento = traumaObito.dsEvento;
        }
      })
      .catch(error => {
        this.toast.error(error.response?.data?.message || "Ocorreu um erro.");
        this.closeModal("modalReclassify");
        this.closeModal("modalReclassifyConfirm");
        this.closeModal("modalRegister");
      })
      .finally(() => this.hideLoading());
  }

  private validateReclassificationFields() {
    this.hasError = false;
    this.errorMessage = "";

    if (!this.reclassificationInput || !this.disagreeJustificationText) {
      this.hasError = true;
      this.errorMessage = "Preencha todos os campos.";

      return;
    }

    this.reclassificationInput = this.reclassificationInput.toUpperCase();
  }

  public reclassify(): void {
    this.validateReclassificationFields();

    if (!this.hasError) {
      this.showLoading();

      const data = {
        agreeClassification: this.modalReclassify.options.agreeClassification,
        basicCauseReclassified: this.reclassificationInput,
        justification: this.disagreeJustificationText,
      };

      TraumaObitoService.reclassify(this.id, data)
        .then(() => {
          this.toast.success("Registro alterado com sucesso");
          this.closeModal("modalReclassify");
          this.closeModal("modalReclassifyConfirm");
          this.closeModal("modalRegister");

          this.doSearch(this.currentPage, this.perPage, this.order, this.sort, this.makeFiltersCriteria());

          this.reclassificationInput = "";
          this.disagreeJustificationText = "";
        })
        .catch(error => this.toast.error(error.response?.data?.message || "Ocorreu um erro."))
        .finally(() => this.hideLoading());
    }
  }

  public setReclassifiedSIM(): void {
    this.showLoading();

    TraumaObitoService.setReclassifiedSIM(this.id)
      .then(() => {
        this.toast.success("Registro alterado com sucesso");
        this.closeModal("modalConfirm");
        this.closeModal("modalRegister");

        this.doSearch(this.currentPage, this.perPage, this.order, this.sort, this.makeFiltersCriteria());
      })
      .catch(error => this.toast.error(error.response?.data?.message || "Ocorreu um erro."))
      .finally(() => this.hideLoading());
  }

  public setDeathTypeReviewed(): void {
    this.showLoading();

    TraumaObitoService.setDeathTypeReviewed(this.id, this.registro.tipoObitoRevisado)
      .then(() => {
        this.toast.success("Registro alterado com sucesso");
        this.closeModal("modalConfirm");

        this.doSearch(this.currentPage, this.perPage, this.order, this.sort, this.makeFiltersCriteria());
      })
      .catch(error => this.toast.error(error.response?.data?.message || "Ocorreu um erro."))
      .finally(() => this.hideLoading());
  }

  public setWorkAccidentReviewed(): void {
    this.showLoading();

    TraumaObitoService.setWorkAccidentReviewed(this.id, this.registro.acidenteTrabalhoRevisado)
      .then(() => {
        this.toast.success("Registro alterado com sucesso");
        this.closeModal("modalConfirm");

        this.doSearch(this.currentPage, this.perPage, this.order, this.sort, this.makeFiltersCriteria());
      })
      .catch(error => this.toast.error(error.response?.data?.message || "Ocorreu um erro."))
      .finally(() => this.hideLoading());
  }

  public viewFilters(): void {
    this.showFilters = !this.showFilters;

    if (this.showFilters) {
      this.makeFiltersRow();
    } else {
      this.table.rows = this.table.rows.slice(1);
    }
  }

  private makeFiltersRow(): void {
    if (this.showFilters) {
      const newRow = {
        numDO: -1,
        sexo: -1,
        idadeObito: -1,
        ufResidencia: -1,
        codigoMunicipioResidencia: -1,
        dataObito: -1,
        causaBasica: -1,
        causaBasicaReclassificada: -1,
        status: -1,
        concordaReclassificacao: -1,
        tipoObito: -1,
        acidenteTrabalho: -1,
        dataRevisao: -1,
        actions: -1,
      };

      this.table.rows = [newRow, ...this.table.rows];
    }
  }

  public clearFilters() {
    this.filters.status = [];
    this.filters.sexo = [];
    this.filters.ufResidencia = [];

    this.filtersNames.forEach(i => {
      this.filters[i] = undefined;
    });

    this.clearSearchParams();
    this.applyFilters();
  }

  public applyFilters() {
    const criteria = this.makeFiltersCriteria();

    this.currentPage = 1;

    this.list(this.currentPage, this.perPage, this.order, this.sort, criteria);
  }

  public exportCSV() {
    this.showLoading();

    const criteria = this.makeFiltersCriteria();

    TraumaObitoService.exportCSV(criteria, this.order, this.sort)
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = "obitos.csv";
        link.click();

        URL.revokeObjectURL(url);
      })
      .catch(() => this.toast.error("Ocorreu um erro ao gerar o CSV."))
      .finally(() => this.hideLoading());
  }

  public makeFiltersCriteria() {
    const criteria: FilterCriteria = {};

    if (this.filters.status?.length) {
      criteria.status = this.filters.status.map(i => i.code);
      this.setSearchParams("filter[status]", criteria.status);
    }

    if (this.filters.concordaReclassificacao?.length) {
      criteria.concordaReclassificacao = this.filters.concordaReclassificacao.map(i => i.code);
      this.setSearchParams("filter[concordaReclassificacao]", criteria.concordaReclassificacao);
    }

    if (this.filters.sexo?.length) {
      criteria.sexo = this.filters.sexo.map(i => i.code);
      this.setSearchParams("filter[sexo]", criteria.sexo);
    }

    if (this.filters.ufResidencia?.length) {
      criteria.ufResidencia = this.filters.ufResidencia.map(i => i.code);
      this.setSearchParams("filter[ufResidencia]", criteria.ufResidencia);
    }

    if (this.filters.numDO?.trim()) {
      criteria.numDO = this.filters.numDO;
      this.setSearchParams("filter[numDO]", criteria.numDO);
    }

    if (this.filters.idadeObito?.trim()) {
      criteria.idadeObito = this.filters.idadeObito;
      this.setSearchParams("filter[idadeObito]", criteria.idadeObito);
    }

    if (this.filters.codigoMunicipioResidencia?.trim()) {
      criteria.codigoMunicipioResidencia = this.filters.codigoMunicipioResidencia;
      this.setSearchParams("filter[codigoMunicipioResidencia]", criteria.codigoMunicipioResidencia);
    }

    if (this.filters.dataObito?.trim()) {
      criteria.dataObito = UtilDate.formatToIsoString(UtilDate.stringToDate(this.filters.dataObito, "DD/MM/YYYY"));
      this.setSearchParams("filter[dataObito]", criteria.dataObito);
    }

    if (this.filters.causaBasica?.trim()) {
      criteria.causaBasica = this.filters.causaBasica;
      this.setSearchParams("filter[causaBasica]", criteria.causaBasica);
    }

    if (this.filters.causaBasicaReclassificada?.trim()) {
      criteria.causaBasicaReclassificada = this.filters.causaBasicaReclassificada;
      this.setSearchParams("filter[causaBasicaReclassificada]", criteria.causaBasicaReclassificada);
    }

    if (this.filters.tipoObito?.trim()) {
      criteria.tipoObito = this.filters.tipoObito;
      this.setSearchParams("filter[tipoObito]", criteria.tipoObito);
    }

    if (this.filters.acidenteTrabalho?.length) {
      criteria.acidenteTrabalho = this.filters.acidenteTrabalho.map(i => i.code);
      this.setSearchParams("filter[acidenteTrabalho]", criteria.acidenteTrabalho);
    }

    if (this.filters.dataRevisao?.trim()) {
      criteria.dataRevisao = UtilDate.formatToIsoString(UtilDate.stringToDate(this.filters.dataRevisao, "DD/MM/YYYY"));
      this.setSearchParams("filter[dataRevisao]", criteria.dataRevisao);
    }

    return criteria;
  }

  private setSearchParams(paramName: string, value: string | string[] | number | number[]) {
    const query = { ...this.$route.query };

    if (value === undefined) {
      delete query[paramName];
    }

    return this.$router
      .replace({
        query: {
          ...query,
          [paramName]: value || undefined,
        },
      })
      .catch(() => {});
  }

  private async clearSearchParams() {
    await this.$router.replace({ query: {} }).catch(() => {});

    await this.setSearchParams("limit", this.perPage);
    await this.setSearchParams("offset", this.currentPage);
    await this.setSearchParams("order", this.order);
    await this.setSearchParams("sort", this.sort);
  }

  public myFilterPredicate(item, searchText) {
    return !searchText ? true : item.toLowerCase().startsWith(searchText.toLowerCase());
  }

  private defineCidsInterval(agreeClassification: boolean, agrupamentoML: string): void {
    if (agreeClassification === undefined || !agrupamentoML) return;

    const ranges = agrupamentoML.split(", ").map(r => r.split(" à "));

    const filtered = this.cids.filter(cid => {
      return ranges.some(range => {
        const isInRange = cid.value >= range[0] && cid.value <= range[1];
        return agreeClassification ? isInRange : !isInRange;
      });
    });

    this.cidsToShow = filtered;
  }
}
